/**
 * NetCartas API Service
 *
 * DATE: 2025-12-17 | Author: IA + Luiz Couto
 *
 * Serviço para comunicação com a API do NetCartas
 * Usado para buscar userId por nickname e outras operações
 *
 * SECURITY NOTE: All API calls go through the Worker proxy (/api/netcartas/*)
 * The API token is stored securely on the server side and NEVER exposed to the client.
 *
 * TODO [SECURITY]: Implementar autenticação real do usuário.
 * Atualmente confiamos no netcartasId enviado pelo client (MVP).
 * Para produção, validar sessão/token do NetCartas no backend.
 */

// ============================================================================
// CONFIGURAÇÃO
// ============================================================================

/**
 * URL base do Worker proxy que faz chamadas seguras para a API do NetCartas
 * O token de autenticação é adicionado pelo servidor, nunca exposto no client.
 */
const WORKER_API_BASE = import.meta.env.DEV
  ? 'http://localhost:8881/api/netcartas'
  : 'https://domino-server.pixlabs.workers.dev/api/netcartas';

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Resposta da API de busca de usuário por nickname
 * A API retorna { success: boolean, userAccount: {...} }
 */
export interface UserByNicknameResponse {
  id: number;
  name: string;
  email?: string;
  fullName?: string;
  profilePhoto?: string;
  member?: boolean;
}

/**
 * Estrutura real da resposta da API do NetCartas
 */
interface NetCartasApiResponse {
  success: boolean;
  userAccount?: UserByNicknameResponse;
  error?: string;
}

// ============================================================================
// FUNÇÕES
// ============================================================================

/**
 * Busca informações do usuário pelo nickname no NetCartas
 *
 * @param nickname - Nickname/apelido do usuário
 * @returns Dados do usuário ou null se não encontrado
 *
 * Endpoint: GET /api/1.0/user/admin/nickname/{nickname}
 * Header: NETCARTAS_API_GATEWAY_MICROSERVICES_TOKEN
 */
export async function getUserByNickname(nickname: string): Promise<UserByNicknameResponse | null> {
  try {
    console.log(`🔍 [2025-12-17] Buscando userId para nickname: ${nickname}`);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/user/admin/nickname/${encodeURIComponent(nickname)}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.log(`ℹ️ [2025-12-17] Usuário não encontrado: ${nickname}`);
        return null;
      }
      console.error(`❌ [2025-12-17] Erro ao buscar usuário: ${response.status}`);
      return null;
    }

    // A API retorna { success: boolean, userAccount: {...} }
    const apiResponse: NetCartasApiResponse = await response.json();

    if (!apiResponse.success || !apiResponse.userAccount) {
      console.log(`ℹ️ [2025-12-17] Usuário não encontrado ou erro: ${nickname}`);
      return null;
    }

    const userData = apiResponse.userAccount;
    console.log(`✅ [2025-12-17] Usuário encontrado: ${nickname} -> ID ${userData.id}`);
    return userData;

  } catch (error) {
    console.error(`❌ [2025-12-17] Erro de conexão ao buscar usuário:`, error);
    return null;
  }
}

/**
 * Verifica se a API do NetCartas está acessível (via Worker proxy)
 *
 * @returns true se API está respondendo
 */
export async function checkNetCartasApiHealth(): Promise<boolean> {
  try {
    // Simple health check - just try to connect via proxy
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    // Chamada via Worker proxy (token adicionado pelo servidor)
    const response = await fetch(`${WORKER_API_BASE}/user/admin/nickname/test`, {
      method: 'GET',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Even 404 means API is working (user not found is expected)
    return response.status === 404 || response.ok;

  } catch {
    return false;
  }
}

/**
 * Retorna a URL base do Worker proxy para a API do NetCartas
 */
export function getNetCartasApiBase(): string {
  return WORKER_API_BASE;
}
